/**
 * Preferences Service
 * 
 * Handles syncing theme and layout preferences to backend when user is authenticated.
 * This service provides a centralized way to persist user preferences across devices.
 */

import { getApiBaseUrl } from '../config';
import type { Theme, WorkspaceLayout } from '../store/daw-store';

export interface UserPreferences {
  theme: Theme;
  layout: WorkspaceLayout;
  customThemes: Theme[];
  customLayouts: WorkspaceLayout[];
}

/**
 * Check if user is authenticated
 * TODO: Replace with actual authentication check when auth system is implemented
 */
const isAuthenticated = (): boolean => {
  // Placeholder: Check for auth token in localStorage
  const tokens = localStorage.getItem('music-legend-tokens');
  return !!tokens;
};

/**
 * Get authentication headers
 * TODO: Replace with actual token retrieval when auth system is implemented
 */
const getAuthHeaders = (): HeadersInit => {
  try {
    const tokens = JSON.parse(localStorage.getItem('music-legend-tokens') || '{}');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tokens.access_token || ''}`,
    };
  } catch {
    return {
      'Content-Type': 'application/json',
    };
  }
};

/**
 * Sync theme preference to backend
 * Validates: Requirements 9.4
 */
export const syncThemeToBackend = async (theme: Theme): Promise<void> => {
  if (!isAuthenticated()) {
    console.log('User not authenticated, skipping theme sync to backend');
    return;
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/preferences/theme`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ theme }),
    });

    if (!response.ok) {
      throw new Error(`Failed to sync theme: ${response.statusText}`);
    }

    console.log('Theme synced to backend successfully');
  } catch (error) {
    console.error('Failed to sync theme to backend:', error);
    // Don't throw - localStorage persistence already succeeded
  }
};

/**
 * Sync layout preference to backend
 * Validates: Requirements 9.4
 */
export const syncLayoutToBackend = async (layout: WorkspaceLayout): Promise<void> => {
  if (!isAuthenticated()) {
    console.log('User not authenticated, skipping layout sync to backend');
    return;
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/preferences/layout`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ layout }),
    });

    if (!response.ok) {
      throw new Error(`Failed to sync layout: ${response.statusText}`);
    }

    console.log('Layout synced to backend successfully');
  } catch (error) {
    console.error('Failed to sync layout to backend:', error);
    // Don't throw - localStorage persistence already succeeded
  }
};

/**
 * Sync custom themes to backend
 * Validates: Requirements 9.4
 */
export const syncCustomThemesToBackend = async (customThemes: Theme[]): Promise<void> => {
  if (!isAuthenticated()) {
    console.log('User not authenticated, skipping custom themes sync to backend');
    return;
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/preferences/custom-themes`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ customThemes }),
    });

    if (!response.ok) {
      throw new Error(`Failed to sync custom themes: ${response.statusText}`);
    }

    console.log('Custom themes synced to backend successfully');
  } catch (error) {
    console.error('Failed to sync custom themes to backend:', error);
    // Don't throw - localStorage persistence already succeeded
  }
};

/**
 * Sync custom layouts to backend
 * Validates: Requirements 9.4
 */
export const syncCustomLayoutsToBackend = async (customLayouts: WorkspaceLayout[]): Promise<void> => {
  if (!isAuthenticated()) {
    console.log('User not authenticated, skipping custom layouts sync to backend');
    return;
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/preferences/custom-layouts`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ customLayouts }),
    });

    if (!response.ok) {
      throw new Error(`Failed to sync custom layouts: ${response.statusText}`);
    }

    console.log('Custom layouts synced to backend successfully');
  } catch (error) {
    console.error('Failed to sync custom layouts to backend:', error);
    // Don't throw - localStorage persistence already succeeded
  }
};

/**
 * Load preferences from backend
 * Validates: Requirements 9.5
 */
export const loadPreferencesFromBackend = async (): Promise<UserPreferences | null> => {
  if (!isAuthenticated()) {
    console.log('User not authenticated, skipping preferences load from backend');
    return null;
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/preferences`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to load preferences: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Preferences loaded from backend successfully');
    return data.preferences;
  } catch (error) {
    console.error('Failed to load preferences from backend:', error);
    return null;
  }
};

/**
 * Sync all preferences to backend
 * Convenience method to sync everything at once
 */
export const syncAllPreferencesToBackend = async (preferences: UserPreferences): Promise<void> => {
  if (!isAuthenticated()) {
    console.log('User not authenticated, skipping preferences sync to backend');
    return;
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/preferences`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ preferences }),
    });

    if (!response.ok) {
      throw new Error(`Failed to sync preferences: ${response.statusText}`);
    }

    console.log('All preferences synced to backend successfully');
  } catch (error) {
    console.error('Failed to sync preferences to backend:', error);
    // Don't throw - localStorage persistence already succeeded
  }
};
